import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

import { FormUnsavedConfirmationWrapper } from '../../../../components/pages/backoffice/form-unsaved-confirmation-wrapper.jsx';
import CancelButton from '../../../../components/buttons/cancel-button.jsx';
import Heading from '../../../../components/typography/heading.jsx';
import { Button } from '../../../../components/ui/button.jsx';
import { Checkbox } from '../../../../components/ui/checkbox.jsx';
import FormCard from '../../../../components/ui/form-card.jsx';
import FormFooter from '../../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/ui/form.jsx';
import { Input } from '../../../../components/ui/input.jsx';
import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { queryClient } from '../../../../services/query/client.js';

export function ShopsCreate() {
  const navigate = useNavigate();

  const formCreateShop = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      number: null,
      type: '',
      image: '',
      status: true,
    },
    resolver: yupResolver(
      yup.object().shape({
        name: yup
          .string()
          .min(3, 'Nome tem de ter pelo menos 3 caracteres')
          .max(80, 'Nome não pode ultrapassar 80 caracteres')
          .required('Nome é um campo obrigatório'),
        number: yup
          .number()
          .integer('Número inválido')
          .required('Número é um campo obrigatório'),
      }),
    ),
  });

  const mutationCreateShop = useMutation({
    mutationFn: async (data) => {
      const { data: newShopRes } = await msCatalystApi.post(`/shops`, data);
      return newShopRes.data;
    },
    onSuccess: (data) => {
      toast.success('Loja adicionada com successo!');
      queryClient.setQueryData(['shop'], data);

      navigate(`/backoffice/admin/shops/${data.ROWID}`);
    },
    onError: (err) => {
      if (
        err?.response?.data?.message ===
        'Duplicate value for number. Please give a different value'
      ) {
        toast.error(`Já existe uma lojá com esse número`, {
          description: 'Por favor, altere o número de loja',
        });
      } else if (err?.response?.data) {
        toast.error(`Erro ao adicionar loja: ${err.data.message}`, {
          description: 'Tente novamente mais tarde',
        });
      } else {
        toast.error('Erro ao adicionar loja', {
          description: 'Tente novamente mais tarde',
        });
      }
    },
  });

  const onFormSubmit = async (data) => {
    mutationCreateShop.mutate(data);
  };

  return (
    <FormUnsavedConfirmationWrapper form={formCreateShop}>
      <Heading className="pb-6">Criar Loja</Heading>

      <Form {...formCreateShop}>
        <form onSubmit={formCreateShop.handleSubmit(onFormSubmit)}>
          <div className="flex flex-col space-y-10">
            <div className="flex flex-col gap-5">
              <h2 className="text-xl font-medium">Dados Gerais</h2>
              <FormCard>
                <FormField
                  control={formCreateShop.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Nome</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateShop.control}
                  name="number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Número</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateShop.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateShop.control}
                  name="image"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Imagem URL</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formCreateShop.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estado</FormLabel>
                      <div className="flex h-10 flex-row items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            disabled={formCreateShop.formState.isSubmitting}
                            id="status"
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked)
                            }
                          />
                        </FormControl>
                        <FormLabel className="space-y-0">Ativa</FormLabel>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCard>
            </div>
          </div>

          <FormFooter>
            <CancelButton />
            <Button
              type="submit"
              disabled={
                formCreateShop.formState.isSubmitting ||
                !formCreateShop.formState.isDirty
              }
              isLoading={formCreateShop.formState.isSubmitting}
            >
              <PlusIcon />
              Criar
            </Button>
          </FormFooter>
        </form>
      </Form>
    </FormUnsavedConfirmationWrapper>
  );
}
